import clsx from "~/util/html/style";
import {
  children,
  createUniqueId,
  type JSX,
  type Ref,
  splitProps,
  type ComponentProps,
  type ParentProps,
  mergeProps,
  Show,
  createSignal,
  untrack,
} from "solid-js";
import { BaselineAligner } from "~/common/baseline_aligner";
import { LABEL_CLASS } from "~/common/form";
import { Icon, type IconName } from "~/icon";
import { evaluateEquation } from "~/util/equation";
import { assignRefs } from "~/util/solid";
import { Button, IconButton } from "./button";
import { todayInTimezone } from "~/util/date";

export function BulkSelectButtons(props: { onSelectAll: () => void; onClear: () => void }) {
  return (
    <div class="flex gap-2">
      <Button type="button" variant="plain-primary" size="xs" onClick={props.onSelectAll}>
        All
      </Button>
      <span class="text-sm text-typography-secondary">/</span>
      <Button type="button" variant="plain-primary" size="xs" onClick={props.onClear}>
        None
      </Button>
    </div>
  );
}

const RADIUS_CLASS = {
  full: "rounded-full",
  // The chat composer's shape: a pill at one line (the radius clamps to half
  // the height), still generously rounded once the textarea grows.
  "3xl": "rounded-3xl",
  lg: "rounded-lg",
} as const;

export function makeInputClass(
  options: {
    compact?: boolean;
    additionalClasses?: string;
    py?: "unset";
    minHeight?: "unset";
    // Pill ("full") only for search-style inputs; data-entry fields stay "lg".
    radius?: keyof typeof RADIUS_CLASS;
    // Drops the has-* states that let a wrapper mirror the control inside it.
    // A select's only descendants are its options, so there one disabled
    // option (a placeholder) would grey out the whole select.
    ownStateOnly?: boolean;
  } = {},
) {
  return clsx(
    "border border-brand-subtle bg-white text-typography-primary outline-none focus-visible:ring-2 focus-visible:ring-brand-focus disabled:bg-brand-disabled data-dark:border-brand-subtle-dark data-dark:bg-gray-900 data-dark:text-typography-primary-dark",
    !options.ownStateOnly &&
      "has-focus-visible:ring-2 has-focus-visible:ring-brand-focus has-disabled:bg-brand-disabled",
    RADIUS_CLASS[options.radius ?? "lg"],
    options.compact ? "px-2" : "px-3",
    options.minHeight !== "unset" && (options.compact ? "min-h-input-sm" : "min-h-input"),
    options.py !== "unset" && "py-0",
    options.additionalClasses,
  );
}

type TodayButtonProps =
  | { showTodayButton: true; timezone: string }
  | { showTodayButton?: false; timezone?: never };

export function Input(
  _props: Omit<ComponentProps<"input">, "ref" | "onInput" | "width"> &
    TodayButtonProps & {
      beforeText?: string;
      // A leading icon inside the field, e.g. the sparkle on an ask box.
      beforeIcon?: IconName;
      afterText?: string;
      growWithContent?: boolean;
      clearable?: boolean;
      dark?: boolean;
      compact?: boolean;
      // A heading-sized value, for the one input that names the thing a page edits.
      large?: boolean;
      radius?: keyof typeof RADIUS_CLASS;
      ref?: Ref<HTMLInputElement>;
      onInput?: JSX.EventHandler<HTMLInputElement, InputEvent>;
      width?: "full" | "unset" | "fit";
    },
) {
  const withDefaults = mergeProps({ width: "fit" }, _props);
  const [props, inputProps] = splitProps(withDefaults, [
    "class",
    "beforeText",
    "beforeIcon",
    "afterText",
    "growWithContent",
    "clearable",
    "showTodayButton",
    "timezone",
    "dark",
    "compact",
    "large",
    "radius",
    "ref",
    "onInput",
    "width",
  ]);

  let ref!: HTMLInputElement;

  const [hasTypedText, setHasTypedText] = createSignal(Boolean(untrack(() => inputProps.value)));
  // Controlled inputs drive the clear button from the `value` prop so it also
  // hides when the value is cleared externally; uncontrolled ones track typing.
  const canClear = () =>
    inputProps.value != null ? String(inputProps.value).length > 0 : hasTypedText();

  return (
    <div
      class={clsx(
        "group/input flex items-center",
        makeInputClass({
          compact: props.compact ?? false,
          radius: props.radius,
        }),
        props.compact ? "h-input-sm" : props.large ? "h-input-lg" : "h-input",
        props.width === "full" && "w-full",
        props.width === "fit" && "w-fit",
        props.class,
      )}
      data-dark={props.dark}
    >
      <Show when={props.beforeIcon}>
        {(name) => <Icon name={name()} size="sm" class="mr-2 shrink-0 text-typography-secondary" />}
      </Show>
      <Show when={props.beforeText}>
        <div class="flex items-center pr-1 text-sm text-typography-secondary">
          <p>{props.beforeText}</p>
        </div>
      </Show>

      <input
        ref={assignRefs((el) => (ref = el), props.ref)}
        class={clsx(
          "w-full border-none bg-transparent px-0 group-data-dark/input:placeholder:text-typography-secondary-dark focus:ring-0 focus:outline-none",
          props.growWithContent && "field-sizing-content",
          props.compact && "text-sm",
          props.large && "text-lg font-medium",
        )}
        {...inputProps}
        onInput={(event) => {
          props.onInput?.(event);
          setHasTypedText(Boolean(event.currentTarget.value));
        }}
      />

      <Show when={props.afterText}>
        <div class="flex items-center pl-1 text-sm text-typography-secondary">
          <p>{props.afterText}</p>
        </div>
      </Show>

      <Show when={props.clearable && canClear()}>
        <IconButton
          variant="plain-primary"
          size={props.compact ? "small" : "medium"}
          iconName="x"
          aria-label="Clear"
          onClick={() => {
            ref.value = "";
            const event = new Event("input", { bubbles: true });
            ref.dispatchEvent(event);
          }}
        />
      </Show>

      <Show when={props.showTodayButton}>
        <Button
          type="button"
          variant="secondary"
          size="xs"
          class="ml-1 shrink-0"
          onClick={() => {
            ref.value = todayInTimezone(props.timezone);
            ref.dispatchEvent(new Event("input", { bubbles: true }));
          }}
        >
          Today
        </Button>
      </Show>
    </div>
  );
}

/**
 * Strips characters that aren't valid in a decimal value (digits, one dot, up to 2 decimals,
 * optional leading minus), preserving caret position. Used for money and percent text inputs
 * so they reject letters without resorting to `type="number"` (and its spinner arrows).
 */
export function sanitizeDecimalValue(el: HTMLInputElement, allowNegative: boolean) {
  const before = el.value;
  const caret = el.selectionStart ?? before.length;

  let sanitized = "";
  let hasDot = false;
  let decimals = 0;
  let removedBeforeCaret = 0;

  for (let i = 0; i < before.length; i++) {
    const ch = before[i]!;
    if (ch >= "0" && ch <= "9") {
      if (hasDot && decimals >= 2) {
        if (i < caret) removedBeforeCaret++;
        continue;
      }
      if (hasDot) decimals++;
      sanitized += ch;
    } else if (ch === "." && !hasDot) {
      hasDot = true;
      sanitized += ch;
    } else if (ch === "-" && allowNegative && sanitized === "") {
      sanitized += ch;
    } else {
      if (i < caret) removedBeforeCaret++;
    }
  }

  if (sanitized !== before) {
    el.value = sanitized;
    el.setSelectionRange(caret - removedBeforeCaret, caret - removedBeforeCaret);
  }
}

// An operator anywhere past a leading sign means the user is typing arithmetic (`120+45.50`),
// which the blur/Enter evaluation turns back into an amount.
const EQUATION_OPERATOR = /[+*/()]|.-/;

function isEquationInput(value: string) {
  return EQUATION_OPERATOR.test(value.trim());
}

function sanitizeEquationValue(el: HTMLInputElement) {
  const sanitized = el.value.replace(/[^\d.+\-*/() ,]/g, "");
  if (sanitized !== el.value) el.value = sanitized;
}

/** Replaces a typed expression with the amount it works out to and reports whether there was
 *  one. A negative answer in a positive-only field is cleared for the user to retype: flipping
 *  its sign would quietly submit the opposite amount. */
function evaluateMoneyEquation(el: HTMLInputElement, allowNegative: boolean) {
  if (!isEquationInput(el.value)) return false;
  const result = evaluateEquation(el.value);
  if (!result) return false;

  const cents = Math.round(result.value * 100);
  el.value = cents < 0 && !allowNegative ? "" : centsToMoneyInput(cents);
  el.dispatchEvent(new Event("input", { bubbles: true }));
  return true;
}

function formatMoneyOnBlur(el: HTMLInputElement) {
  const raw = el.value.trim();
  if (raw === "" || raw === "-") return;
  const num = parseFloat(raw);
  if (Number.isFinite(num)) {
    el.value = num.toFixed(2);
    el.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

export function centsToMoneyInput(cents: number | null | undefined): string {
  return cents != null ? (cents / 100).toFixed(2) : "";
}

/** What is left of an amount once its dollar sign, separators and spaces are gone: a sign, whole
 *  dollars, and at most two decimals, any of them optional — so `.5` is fifty cents, and `12abc`
 *  or `1.234` is no amount at all rather than the part of one parseFloat would have kept. */
const MONEY_PATTERN = /^[+-]?\d*(\.\d{0,2})?$/;

export function moneyInputToCents(value: string): number | null {
  // A pasted amount can carry the dollar sign and the separators it was copied with, so those go
  // before anything is checked.
  const stripped = value.replace(/[$,\s]/g, "");
  if (!MONEY_PATTERN.test(stripped)) return null;

  const parsed = parseFloat(stripped);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
}

export function MoneyInput(
  _props: Omit<
    ComponentProps<"input">,
    "ref" | "type" | "inputMode" | "onInput" | "onChange" | "onBlur" | "onKeyDown" | "width"
  > & {
    dark?: boolean;
    compact?: boolean;
    ref?: Ref<HTMLInputElement>;
    onInput?: JSX.EventHandler<HTMLInputElement, InputEvent>;
    // change always targets the input itself, so consumers can read `event.target.value`
    onChange?: (
      event: Event & { currentTarget: HTMLInputElement; target: HTMLInputElement },
    ) => void;
    onBlur?: JSX.EventHandler<HTMLInputElement, FocusEvent>;
    onKeyDown?: JSX.EventHandler<HTMLInputElement, KeyboardEvent>;
    width?: "full" | "unset" | "fit";
    allowNegative?: boolean;
    /** Lets the user type arithmetic (`120+45.50`), worked out on change, blur or Enter. Off by
     *  default: turn it on per field once that screen has been checked with it. */
    allowEquations?: boolean;
  },
) {
  const withDefaults = mergeProps({ width: "fit" }, _props);
  const [props, inputProps] = splitProps(withDefaults, [
    "class",
    "dark",
    "compact",
    "ref",
    "onInput",
    "onChange",
    "onBlur",
    "onKeyDown",
    "width",
    "allowNegative",
    "allowEquations",
  ]);

  let ref!: HTMLInputElement;
  const evaluate = () =>
    props.allowEquations === true && evaluateMoneyEquation(ref, props.allowNegative ?? false);

  return (
    <div
      class={clsx(
        "group/input flex items-center",
        makeInputClass({
          compact: props.compact ?? false,
        }),
        props.compact ? "h-input-sm" : "h-input",
        props.width === "full" && "w-full",
        props.width === "fit" && "w-fit",
        props.class,
      )}
      data-dark={props.dark}
    >
      <div class="flex items-center pr-1 text-sm text-typography-secondary">
        <p>$</p>
      </div>

      <input
        ref={assignRefs((el) => (ref = el), props.ref)}
        type="text"
        inputMode="decimal"
        class={clsx(
          "w-full border-none bg-transparent px-0 group-data-dark/input:placeholder:text-typography-secondary-dark focus:ring-0 focus:outline-none",
          props.compact && "text-sm",
        )}
        {...inputProps}
        onInput={(event) => {
          if (props.allowEquations && isEquationInput(event.currentTarget.value)) {
            sanitizeEquationValue(event.currentTarget);
          } else {
            sanitizeDecimalValue(event.currentTarget, props.allowNegative ?? false);
          }
          props.onInput?.(event);
        }}
        onChange={(event) => {
          // change fires before blur, so a consumer committing on change must get the result
          evaluate();
          props.onChange?.(
            event as Event & { currentTarget: HTMLInputElement; target: HTMLInputElement },
          );
        }}
        onKeyDown={(event) => {
          // Enter on an expression computes it instead of submitting the form.
          if (event.key === "Enter" && evaluate()) {
            event.preventDefault();
          }
          props.onKeyDown?.(event);
        }}
        onBlur={(event) => {
          evaluate();
          formatMoneyOnBlur(ref);
          props.onBlur?.(event);
        }}
      />
    </div>
  );
}

export function Textarea(
  _props: ComponentProps<"textarea"> & {
    width?: "full" | "unset" | "fit";
    dark?: boolean;
    growWithContent?: boolean;
    compact?: boolean;
    minHeight?: "unset";
    radius?: keyof typeof RADIUS_CLASS;
  },
) {
  const withDefaults = mergeProps({ width: "default" }, _props);
  const [local, rest] = splitProps(withDefaults, [
    "dark",
    "growWithContent",
    "class",
    "compact",
    "minHeight",
    "width",
    "radius",
  ]);

  return (
    <textarea
      class={clsx(
        "data-dark:placeholder:text-typography-secondary-dark",
        // makeInputClass zeroes vertical padding (py-0) to center single-line inputs via min-height; a
        // textarea top-aligns instead, so we own the padding here. The value vertically centers one line
        // within the min-height (accounting for the 1px border) and doubles as multi-line breathing room,
        // which is why compact also forces text-sm — the line-height has to match the size.
        makeInputClass({
          compact: local.compact ?? false,
          py: "unset",
          minHeight: local.minHeight,
          radius: local.radius,
        }),
        local.width === "full" && "w-full",
        local.width === "fit" && "w-fit",
        local.growWithContent && "field-sizing-content",
        local.compact ? "py-0.75 text-sm" : "py-1.25",
        local.class,
      )}
      data-dark={local.dark || undefined}
      {...rest}
    />
  );
}

export function CheckboxLabel(props: ComponentProps<"label">) {
  return (
    <label class="mb-0 font-medium text-typography-primary" {...props}>
      {props.children}
    </label>
  );
}

export function Checkbox(
  _props: Omit<ComponentProps<"input">, "type"> & {
    children: JSX.Element;
    size?: "medium" | "none";
    // Solid assigns `indeterminate` as a live DOM property (it is in its Properties set), but omits it
    // from the input JSX types. Declaring it here is enough — the spread below applies it reactively.
    indeterminate?: boolean;
  },
) {
  const mergedProps = mergeProps({ size: "medium" }, _props);
  const [props, inputProps] = splitProps(mergedProps, ["class", "children", "size"]);

  return (
    <div
      class={clsx(
        "relative flex items-center gap-3",
        // min-, not fixed: a label that wraps (the SMS sign-up consent) grows the row instead of
        // spilling over the fields around it.
        props.size === "medium" && "min-h-input",
        props.class,
      )}
    >
      <input
        type="checkbox"
        class={clsx(
          "shrink-0 rounded-sm border border-brand-subtle bg-white checked:border-brand-primary checked:bg-brand-primary focus-visible:ring-2 focus-visible:ring-brand-focus disabled:bg-brand-disabled",
          props.class,
        )}
        {...inputProps}
      />

      {props.children}
    </div>
  );
}

export function CheckboxGroup(
  _props: ComponentProps<"fieldset"> & {
    label: JSX.Element;
    orientation?: "horizontal" | "vertical";
    wrap?: boolean;
    compact?: boolean;
  },
) {
  const withDefaults = mergeProps({ orientation: "vertical" }, _props);
  const [props, fieldsetProps] = splitProps(withDefaults, [
    "label",
    "children",
    "orientation",
    "wrap",
    "compact",
  ]);
  return (
    <fieldset {...fieldsetProps}>
      {props.label}
      <div
        class={clsx(
          "flex",
          props.orientation === "vertical" ? "flex-col gap-2" : props.compact ? "gap-2" : "gap-4",
          props.wrap ? "flex-wrap" : props.orientation === "horizontal" ? "h-input" : undefined,
        )}
      >
        {props.children}
      </div>
    </fieldset>
  );
}

export function CheckboxGroupLabel(props: ParentProps) {
  return <legend class={clsx(LABEL_CLASS, "mb-1")}>{props.children}</legend>;
}

export function CheckboxGroupOption(
  _props: Omit<ComponentProps<typeof Checkbox>, "id" | "children"> & { label: string },
) {
  const [props, inputProps] = splitProps(_props, ["label"]);
  const id = createUniqueId();

  return (
    <Checkbox id={id} size="none" {...inputProps}>
      <CheckboxLabel for={id}>{props.label}</CheckboxLabel>
    </Checkbox>
  );
}

export function RadioLabel(props: ComponentProps<"label">) {
  return (
    // Laid out inline so a pill or other block-level element in the label sits beside the
    // words rather than dropping to a line of its own.
    <label
      class="mb-0 inline-flex items-center gap-2 font-medium text-typography-primary"
      {...props}
    >
      {props.children}
    </label>
  );
}

export function Radio(
  _props: Omit<ComponentProps<"input">, "type"> & {
    children: JSX.Element;
    size?: "medium" | "none";
  },
) {
  const mergedProps = mergeProps({ size: "medium" }, _props);
  const [props, inputProps] = splitProps(mergedProps, ["class", "children", "size"]);

  return (
    <div class={clsx("flex items-center gap-3", props.size === "medium" && "h-input", props.class)}>
      <input
        type="radio"
        class={clsx(
          "rounded-full border border-brand-subtle bg-white checked:border-brand-primary checked:bg-brand-primary focus-visible:ring-2 focus-visible:ring-brand-focus disabled:bg-brand-disabled",
          props.class,
        )}
        {...inputProps}
      />

      {props.children}
    </div>
  );
}

export function RadioGroup(
  _props: ComponentProps<"fieldset"> & {
    label: JSX.Element;
    orientation?: "horizontal" | "vertical";
    wrap?: boolean;
    compact?: boolean;
  },
) {
  const withDefaults = mergeProps({ orientation: "vertical" }, _props);
  const [props, fieldsetProps] = splitProps(withDefaults, [
    "label",
    "children",
    "orientation",
    "wrap",
    "compact",
  ]);
  return (
    <fieldset {...fieldsetProps}>
      {props.label}
      <div
        class={clsx(
          "flex",
          props.orientation === "vertical" ? "flex-col gap-2" : props.compact ? "gap-2" : "gap-4",
          props.wrap ? "flex-wrap" : props.orientation === "horizontal" ? "h-input" : undefined,
        )}
      >
        {props.children}
      </div>
    </fieldset>
  );
}

export function RadioGroupLabel(props: ParentProps) {
  return <legend class={clsx(LABEL_CLASS, "mb-1")}>{props.children}</legend>;
}

export function RadioGroupOption(
  // The label can carry more than words — a pill, a hint — and everything in it
  // both selects the option when clicked and joins its accessible name.
  _props: Omit<ComponentProps<typeof Radio>, "id" | "children"> & { label: JSX.Element },
) {
  const [props, inputProps] = splitProps(_props, ["label"]);
  const id = createUniqueId();

  return (
    <Radio id={id} size="none" {...inputProps}>
      <RadioLabel for={id}>{props.label}</RadioLabel>
    </Radio>
  );
}

type SelectionCardProps = {
  checked: boolean;
  /** What the card carries under its description, inside the same box: a preview, a summary, a
   *  second line of evidence. The slot lays out its own contents; the card only places it. */
  children?: JSX.Element;
  class?: string;
  compact?: boolean;
  description?: string;
  disabled?: boolean;
  label: string;
  onChange: () => void;
  prefix?: JSX.Element;
  rightSlot?: JSX.Element;
  variant?: "default" | "warning";
};

function selectionCardClass(opts: {
  checked?: boolean;
  compact?: boolean;
  variant?: "default" | "warning";
  class?: string;
}): string {
  return clsx(
    "grid cursor-pointer grid-cols-[auto_1fr_auto] grid-rows-[auto_1fr] items-baseline justify-between rounded-lg border bg-white text-left text-sm shadow-sm transition-colors disabled:cursor-default disabled:opacity-70",
    opts.compact ? "min-h-input px-3 py-2" : "flex-1 p-4",
    opts.checked && opts.variant === "warning"
      ? "border-brand-warning bg-brand-warning-light ring-1 ring-brand-warning"
      : opts.checked
        ? "border-brand-primary ring-1 ring-brand-primary"
        : "border-brand-subtle hover:border-brand-border disabled:hover:border-brand-subtle",
    opts.class,
  );
}

function SelectionCardContents(
  props: Pick<SelectionCardProps, "children" | "description" | "label" | "prefix" | "rightSlot"> & {
    /** Undefined on a card that answers nothing, which draws no check bubble at all. */
    checked?: boolean;
  },
) {
  const slot = children(() => props.children);

  return (
    <>
      <Show when={props.prefix} fallback={<div />}>
        <BaselineAligner class="mr-2">
          <span class="shrink-0">{props.prefix}</span>
        </BaselineAligner>
      </Show>

      <span class="block min-w-0 font-medium text-typography-primary">{props.label}</span>

      <Show
        when={props.rightSlot}
        fallback={
          <Show when={props.checked !== undefined}>
            <span
              class={clsx(
                "ml-2 flex size-5 shrink-0 items-center justify-center rounded-full p-1 text-white",
                props.checked ? "bg-brand-primary" : "bg-white",
              )}
            >
              &ZeroWidthSpace;
              <Icon name="check" size="xs" />
            </span>
          </Show>
        }
      >
        <div class="ml-2 flex items-center">{props.rightSlot}</div>
      </Show>

      <Show when={props.description}>
        <span class="col-span-2 col-start-2 block min-w-0 text-xs text-typography-tertiary">
          {props.description}
        </span>
      </Show>

      <Show when={slot()}>
        <div class="col-span-2 col-start-2 mt-3 min-w-0">{slot()}</div>
      </Show>
    </>
  );
}

function SelectionCardShell(
  props: Omit<SelectionCardProps, "checked" | "onChange"> & {
    checked?: boolean;
    role?: "radio";
    ariaChecked?: boolean;
    onClick: () => void;
  },
) {
  return (
    <button
      type="button"
      role={props.role}
      aria-checked={props.ariaChecked}
      disabled={props.disabled}
      onClick={() => props.onClick()}
      class={selectionCardClass({
        checked: props.checked,
        compact: props.compact,
        variant: props.variant,
        class: props.class,
      })}
    >
      <SelectionCardContents
        checked={props.checked}
        description={props.description}
        label={props.label}
        prefix={props.prefix}
        rightSlot={props.rightSlot}
      >
        {props.children}
      </SelectionCardContents>
    </button>
  );
}

export function RadioCard(props: SelectionCardProps) {
  return (
    <SelectionCardShell
      {...props}
      role="radio"
      ariaChecked={props.checked}
      onClick={props.onChange}
    />
  );
}

// A plain button today, not `role="checkbox"`: which ARIA these announce is a decision for
// their call sites, which is where the sweep to change it would have to land.
export function CheckboxCard(props: SelectionCardProps) {
  return <SelectionCardShell {...props} onClick={props.onChange} />;
}

/** Opens something rather than answering a form, so it announces no state and draws no
 *  check bubble. */
export function ActionCard(
  props: Omit<SelectionCardProps, "checked" | "onChange" | "variant"> & { onClick: () => void },
) {
  return <SelectionCardShell {...props} />;
}

export function ColorRadio(props: {
  checked: boolean;
  color: string;
  label?: string;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => props.onChange()}
      aria-label={props.label || props.color}
      title={props.label || props.color}
      class="size-8 rounded-full border-2 transition-transform hover:scale-110"
      classList={{
        "border-brand-primary": props.checked,
        "border-transparent": !props.checked,
      }}
      style={{ "background-color": props.color }}
    />
  );
}
